import type { Assertion, CaseSourceType } from '../api/evalApi';

type CaseValidationInput = {
  promptId: string;
  promptVersion: number;
  providers: string[];
  assertions: Assertion[];
  sourceType?: CaseSourceType;
  sourceTraceId?: string;
  sourceReviewed?: boolean;
};

export function validateCaseInputs({
  promptId,
  promptVersion,
  providers,
  assertions,
  sourceType,
  sourceTraceId,
  sourceReviewed,
}: CaseValidationInput): string | null {
  if (!promptId.trim()) {
    return 'Prompt ID is required.';
  }

  if (promptVersion <= 0) {
    return 'Prompt version must be greater than 0.';
  }

  if (providers.length === 0) {
    return 'Select at least one provider target.';
  }

  if (assertions.length === 0) {
    return 'Add at least one assertion.';
  }

  for (const [index, assertion] of assertions.entries()) {
    const prefix = `Assertion ${index + 1}`;

    if (
      assertion.type === 'contains' ||
      assertion.type === 'not_contains' ||
      assertion.type === 'regex'
    ) {
      if (typeof assertion.value !== 'string' || !assertion.value.trim()) {
        return `${prefix}: value is required for ${assertion.type}.`;
      }
    }

    if (assertion.type === 'min_length' || assertion.type === 'max_length') {
      const value = Number(assertion.value);
      if (!Number.isFinite(value) || value < 0) {
        return `${prefix}: value must be a non-negative number for ${assertion.type}.`;
      }
    }

    if (assertion.type === 'json_path') {
      if (!assertion.path?.trim()) {
        return `${prefix}: path is required for json_path.`;
      }

      const operator = assertion.operator || 'exists';
      if (!['exists', 'equals', 'contains'].includes(operator)) {
        return `${prefix}: operator must be one of exists, equals, contains.`;
      }

      if (operator === 'equals' && assertion.value === undefined) {
        return `${prefix}: value is required when json_path operator is equals.`;
      }

      if (operator === 'contains' && typeof assertion.value !== 'string') {
        return `${prefix}: value must be text when json_path operator is contains.`;
      }
    }

    if (assertion.type === 'judge' && !assertion.rubric?.trim()) {
      return `${prefix}: rubric is required for judge assertions.`;
    }

    if (assertion.type === 'judge' && assertion.threshold !== undefined) {
      const threshold = Number(assertion.threshold);
      if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1) {
        return `${prefix}: threshold must be a number between 0 and 1 for judge assertions.`;
      }
    }
  }

  if (sourceType === 'production_trace') {
    if (!sourceTraceId?.trim()) {
      return 'Trace ID is required for production trace cases.';
    }
    if (!sourceReviewed) {
      return 'Production trace cases must be reviewed before saving. Confirm the review checkbox.';
    }
  }

  return null;
}

export function normalizeAssertions(assertions: Assertion[]): Assertion[] {
  return assertions.map((assertion) => {
    if (assertion.type === 'min_length' || assertion.type === 'max_length') {
      return {
        ...assertion,
        value: Number(assertion.value),
      };
    }

    if (assertion.type === 'json_path') {
      return {
        ...assertion,
        operator: assertion.operator || 'exists',
      };
    }

    return assertion;
  });
}
